import externalLinkSrc from '@raison/static/icons/external-link-line.svg';
import questionLine from '@raison/static/icons/question-line.svg';
import {AnchorExternal, RootLayout, RsnIconWidget, asREM, styles} from '@raison/ui-kit';
import React from 'react';
import adviserinfo from './imgs/about/adviserinfo.png';
import afsa from './imgs/about/afsa.svg';
import aix from './imgs/about/aix.svg';
import bankOfCanada from './imgs/about/bank-of-canada.svg';
import bvifsc from './imgs/about/bvifsc.png';
import fintrac from './imgs/about/fintrac.svg';
import facebook from './imgs/social/facebook.svg';
import linkedin from './imgs/social/linkedin.svg';
import telegram from './imgs/social/telegram.svg';

const cls = styles({
    aboutScreenContainer: 'flex flex-col gap-6',
    link: 'text-xs text-primary-30 no-underline',
    social: 'flex items-center justify-around',
    social_link: 'flex flex-col items-center no-underline',
    linkLabel: 'text-xs',
    questionIconWrapper: 'flex h-9 w-9 items-center justify-center rounded-full bg-neutral-20',
    questionIcon: 'size-5',
    icon: 'size-12',
    icon_label: 'fv-text-l-bold m-2 text-neutral-100',
    licenses: 'fv-text-l flex flex-col gap-3 text-neutral-60',
    licensesLinks: 'flex h-9 w-full justify-between [&_a]:h-full [&_img]:h-full',
});

export function AboutPage() {
    return (
        <RootLayout title='About Raison'>
            <div className='px-4 py-6'>
                <About />
            </div>
        </RootLayout>
    );
}

export function About() {
    const socialLinks = [
        {
            id: 1,
            title: 'Facebook',
            imageLink: facebook,
            link: 'https://www.facebook.com/raisonapp',
            withKey: true,
        },
        {
            id: 2,
            title: 'Telegram',
            imageLink: telegram,
            link: 'https://t.me/rsn_finance',
            withKey: true,
        },
        {
            id: 3,
            title: 'LinkedIn',
            imageLink: linkedin,
            link: 'https://www.linkedin.com/company/raisonapp',
            withKey: true,
        },
    ];

    const licensesLinks = [
        {
            id: 1,
            img: afsa,
            src: 'https://publicreg.myafsa.com/licence_details/AFSA-A-LA-2023-0004/',
        },
        {
            id: 2,
            img: bvifsc,
            src: 'https://www.bvifsc.vg/regulated-entities/raison-asset-management-corp',
        },
        {
            id: 3,
            img: adviserinfo,
            src: 'https://adviserinfo.sec.gov/firm/summary/282147',
        },
        {
            id: 4,
            img: fintrac,
            src: 'https://fintrac-canafe.canada.ca/msb-esm/reg-eng',
        },
        {
            id: 5,
            img: bankOfCanada,
            src: 'https://www.bankofcanada.ca/core-functions/retail-payments-supervision/list-of-applicants/psp-applicant-details/?account_id=451b037d-f188-f011-b4cc-002248b08a8e',
        },
        {
            id: 6,
            img: aix,
            src: 'https://www.aixkz.com/ru/pages/list-of-trading-members',
        },
    ];

    return (
        <div className={cls.aboutScreenContainer}>
            <div>
                <RsnIconWidget.Sm
                    className='rounded-lg bg-neutral-10 p-3 shadow'
                    as={AnchorExternal}
                    href='https://raison.app/legal'
                    icon={{
                        element: (
                            <div className={cls.questionIconWrapper}>
                                <img src={questionLine} className={cls.questionIcon} />
                            </div>
                        ),
                    }}
                    addon={
                        <div style={{width: asREM(20), height: asREM(20)}}>
                            <img src={externalLinkSrc} width='100%' height='100%' />
                        </div>
                    }
                >
                    <RsnIconWidget.TextBlock heading={'Legal'} details={'Our agreements and other legal resources'} />
                </RsnIconWidget.Sm>
            </div>
            <div className={cls.social}>
                {socialLinks?.map(link => (
                    <AnchorExternal key={link.id} href={link.link} title={link.title} className={cls.social_link}>
                        <img className={cls.icon} src={link.imageLink} alt={link.title} />
                        <span className={`${cls.icon_label} ${cls.linkLabel}`}>{link.title}</span>
                    </AnchorExternal>
                ))}
            </div>
            <div className={cls.licenses}>
                <div>
                    Raison means Raison FinTechnologies Inc. and it’s wholly owned subsidiaries Raison Securities Ltd.
                    (“RKZ”), Raison Asset Management Corp. (“RVG”), UAB Raison Markets (“RLT”), Raison Services OÜ
                    (“REE”), Raison Digital Ltd (“RBZ”) and Imperial Finance Alliance Corp. (“RCA”).
                </div>
                <div>
                    Brokerage services are provided by Raison Securities Ltd., a registered broker dealer regulated by{' '}
                    <AnchorExternal href='https://publicreg.myafsa.com/licence_details/AFSA-A-LA-2023-0004/'>
                        Astana Financial Services Authority
                    </AnchorExternal>{' '}
                    with license AFSA-A-LA-2023-0004.
                </div>
                <div>
                    Asset management services are provided by Raison Asset Management Corp., authorised by{' '}
                    <AnchorExternal href='https://www.bvifsc.vg/regulated-entities/raison-asset-management-corp'>
                        BVI Financial Services Commission
                    </AnchorExternal>{' '}
                    as Approved Investment Manager, Certificate № IBR/AIM/15/0110.
                </div>
                <div>
                    Investment advisory services are provided by RVG registered with{' '}
                    <AnchorExternal href='https://adviserinfo.sec.gov/firm/summary/282147'>
                        U.S. Securities and Exchange Commission
                    </AnchorExternal>
                    , SEC #801-107170 and RKZ regulated by{' '}
                    <AnchorExternal href='https://publicreg.myafsa.com/licence_details/AFSA-A-LA-2023-0004/'>
                        Astana Financial Services Authority
                    </AnchorExternal>{' '}
                    with license AFSA-A-LA-2023-0004.
                </div>
                <div>
                    Virtual currency and payment services for non-EU residents are provided by Imperial Finance Alliance
                    Corp., registered by{' '}
                    <AnchorExternal href='https://fintrac-canafe.canada.ca/msb-esm/reg-eng'>
                        Financial Transactions and Reports Analysis Centre of Canada
                    </AnchorExternal>
                    .
                </div>
                <div>Data processing and KYC verification services are provided by Raison Services OÜ.</div>
                <div>Raison is trading participant of AIX.</div>
            </div>
            <div className={cls.licensesLinks}>
                {licensesLinks.map(item => (
                    <AnchorExternal href={item.src} key={item.id}>
                        <img src={item.img} />
                    </AnchorExternal>
                ))}
            </div>
        </div>
    );
}
